import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { OwnerTable, type ColumnDef } from '../kit/OwnerTable'
import { OwnerFirmConfirm, OwnerHardConfirm } from '../kit/OwnerConfirm'
import { BreakGlassModal } from '../dialogs/BreakGlassModal'
import { useRoles } from '../context/RolesContext'
import { useSupportAccessReason } from '../shell/SupportAccessReasonDialog'
import { navigateOwner } from '../ownerRouter'
import {
  getOwnerPersonDetail,
  type OwnerPersonDetailBundle,
  type OwnerPersonWorkspaceItem,
  type OwnerPersonBreakGlassGrantItem,
} from '../../../services/peopleService'
import { getPriorTicketsByReporter } from '../../../services/supportTicketsService'
import { runOwnerAction, type BreakGlassGrantResult } from '../../../services/ownerConsoleService'
import { calculateNominalDeadline } from '../../../utils/supportSla'
import {
  ownerRequestPersonalExport,
  subscribeToMyExportJobs,
  type ExportJobClient,
} from '../../../services/dataExportClient'
import type { SupportTicket, SupportTicketPriority } from '../../../types/supportTicket'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import TollRoundedIcon from '@mui/icons-material/TollRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded'
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded'
import ExitToAppRoundedIcon from '@mui/icons-material/ExitToAppRounded'
import BlockRoundedIcon from '@mui/icons-material/BlockRounded'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded'
import SportsEsportsRoundedIcon from '@mui/icons-material/SportsEsportsRounded'
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import './ownerPeople.css'

export type PersonTabId =
  | 'overview'
  | 'workspaces'
  | 'subscriptions'
  | 'tickets'
  | 'feedback'
  | 'activity'
  | 'sessions'
  | 'privacy'

const ALL_TABS: Array<{ id: PersonTabId; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'workspaces', label: 'Workspaces' },
  { id: 'subscriptions', label: 'Subscriptions & credits' },
  { id: 'tickets', label: 'Tickets' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'activity', label: 'Activity' },
  { id: 'sessions', label: 'Sessions & devices' },
  { id: 'privacy', label: 'Data & privacy' },
]

const CREDIT_REASON_OPTIONS = [
  { id: 'GOODWILL_ADJUSTMENT', label: 'Goodwill adjustment' },
  { id: 'COMPENSATION_INCIDENT', label: 'Compensation for incident / bug' },
  { id: 'VIP_GRANT', label: 'VIP customer appreciation' },
  { id: 'TESTING_PROMOTION', label: 'Testing / sandbox grant' },
  { id: 'MANUAL_CORRECTION', label: 'Manual ledger correction' },
]

function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message
  if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
    return err.message
  }
  return fallback
}

export interface OwnerPersonDetailProps {
  userId: string
  onClose?: () => void
}

export function OwnerPersonDetail({ userId, onClose }: OwnerPersonDetailProps) {
  const { t } = useNls()
  const { role } = useRoles()
  const { ensureAccessReason } = useSupportAccessReason()
  const [activeTab, setActiveTab] = useState<PersonTabId>('overview')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [detail, setDetail] = useState<OwnerPersonDetailBundle | null>(null)
  const [copiedUid, setCopiedUid] = useState(false)
  const [actionSuccessMsg, setActionSuccessMsg] = useState<string | null>(null)
  const [actionErrorMsg, setActionErrorMsg] = useState<string | null>(null)

  // Header "More" dropdown
  const [openMoreMenu, setOpenMoreMenu] = useState(false)

  // Modals & Drawers state
  const [grantCreditsOpen, setGrantCreditsOpen] = useState(false)
  const [grantCreditAmount, setGrantCreditAmount] = useState(100)
  const [grantReasonEnum, setGrantReasonEnum] = useState('GOODWILL_ADJUSTMENT')
  const [grantCustomReason, setGrantCustomReason] = useState('')
  const [grantCreditsBusy, setGrantCreditsBusy] = useState(false)

  const [resetGamificationOpen, setResetGamificationOpen] = useState(false)
  const [resetAiHistoryOpen, setResetAiHistoryOpen] = useState(false)
  const [forceOnboardingOpen, setForceOnboardingOpen] = useState(false)

  const [signOutEverywhereOpen, setSignOutEverywhereOpen] = useState(false)
  const [passwordResetOpen, setPasswordResetOpen] = useState(false)
  const [toggleDisabledOpen, setToggleDisabledOpen] = useState(false)
  // Workspaces where disabling this account leaves no active owner (server preview).
  const [lastOwnerWorkspaces, setLastOwnerWorkspaces] = useState<Array<{ id: string; name: string }>>([])
  const accountDisabled = detail?.person.disabled === true
  useEffect(() => {
    if (!toggleDisabledOpen || accountDisabled) return
    let cancelled = false
    runOwnerAction({ kind: 'TOGGLE_ACCOUNT_DISABLED', userId, enabled: false, reason: 'Preview: owner impact check', dryRun: true })
      .then((res) => {
        const list = (res.data as { lastActiveOwnerWorkspaces?: Array<{ id: string; name: string }> } | undefined)?.lastActiveOwnerWorkspaces
        if (!cancelled) setLastOwnerWorkspaces(Array.isArray(list) ? list : [])
      })
      .catch(() => {
        if (!cancelled) setLastOwnerWorkspaces([])
      })
    return () => {
      cancelled = true
    }
  }, [toggleDisabledOpen, accountDisabled, userId])

  const [breakGlassOpen, setBreakGlassOpen] = useState(false)
  const [activeRevealToken, setActiveRevealToken] = useState<string | undefined>()

  // Tickets state
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [ticketsLoading, setTicketsLoading] = useState(false)

  // Data export state (TR4 A6)
  const [exportJobs, setExportJobs] = useState<ExportJobClient[]>([])
  const [exporting, setExporting] = useState(false)
  const [exportNotice, setExportNotice] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) return
    return subscribeToMyExportJobs(userId, setExportJobs)
  }, [userId])

  const handleOwnerExport = async () => {
    setExporting(true)
    setExportNotice(null)
    try {
      await ownerRequestPersonalExport(userId, { code: 'USER_REQUEST', note: 'Owner Console export request' })
    } catch (err: unknown) {
      const e = err as { message?: string }
      setExportNotice(e?.message || 'Failed to request export.')
    } finally {
      setExporting(false)
    }
  }

  const detailKey = `${userId || ''}|${activeRevealToken || ''}`
  const [prevDetailKey, setPrevDetailKey] = useState(detailKey)
  if (prevDetailKey !== detailKey) {
    setPrevDetailKey(detailKey)
    if (userId) {
      setLoading(true)
      setError(null)
    }
  }

  const loadDetail = useCallback(
    async (token?: string) => {
      if (!userId) return
      const reason = await ensureAccessReason({
        key: `PERSON:${userId}`,
        defaultCode: 'SUPPORT_TICKET',
        onCancel: onClose,
      })
      if (!reason) {
        setLoading(false)
        return
      }
      setLoading(true)
      setError(null)
      try {
        const res = await getOwnerPersonDetail(userId, token, undefined, reason)
        setDetail(res)
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load user detail')
      } finally {
        setLoading(false)
      }
    },
    [userId, ensureAccessReason, onClose],
  )

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    void (async () => {
      const reason = await ensureAccessReason({
        key: `PERSON:${userId}`,
        defaultCode: 'SUPPORT_TICKET',
        onCancel: onClose,
      })
      if (!reason || cancelled) {
        if (!reason && !cancelled) setLoading(false)
        return
      }
      try {
        const res = await getOwnerPersonDetail(userId, activeRevealToken, undefined, reason)
        if (!cancelled) {
          setDetail(res)
          setLoading(false)
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load user detail')
          setLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [userId, activeRevealToken, ensureAccessReason, onClose])

  const ticketsKey = `${activeTab === 'tickets' ? 'tickets' : 'other'}|${userId || ''}`
  const [prevTicketsKey, setPrevTicketsKey] = useState(ticketsKey)
  if (prevTicketsKey !== ticketsKey) {
    setPrevTicketsKey(ticketsKey)
    if (activeTab === 'tickets' && userId) {
      setTicketsLoading(true)
    }
  }

  // Load tickets on tab change or init
  useEffect(() => {
    if (activeTab === 'tickets' && userId) {
      let cancelled = false
      getPriorTicketsByReporter(userId)
        .then((res) => {
          if (!cancelled) setTickets(res)
        })
        .catch(() => {
          if (!cancelled) setTickets([])
        })
        .finally(() => {
          if (!cancelled) setTicketsLoading(false)
        })
      return () => {
        cancelled = true
      }
    }
  }, [activeTab, userId])

  const handleCopyUid = () => {
    if (!userId) return
    navigator.clipboard?.writeText(userId)
    setCopiedUid(true)
    setTimeout(() => setCopiedUid(false), 2000)
  }

  const handleBack = () => {
    if (onClose) {
      onClose()
    } else {
      navigateOwner({ view: 'people' })
    }
  }

  const showFeedbackToast = (msg: string, isError = false) => {
    if (isError) {
      setActionErrorMsg(msg)
      setTimeout(() => setActionErrorMsg(null), 4000)
    } else {
      setActionSuccessMsg(msg)
      setTimeout(() => setActionSuccessMsg(null), 4000)
    }
  }

  // 1. Grant personal credits
  const handleConfirmGrantCredits = async () => {
    const cap = role === 'support' ? 500 : 100000
    if (grantCreditAmount <= 0 || grantCreditAmount > cap) {
      showFeedbackToast(`Grant amount must be between 1 and ${cap.toLocaleString()} credits.`, true)
      return
    }
    setGrantCreditsBusy(true)
    const reasonText = `${grantReasonEnum}: ${grantCustomReason.trim() || 'Owner console adjustment'}`
    try {
      const res = await runOwnerAction({
        kind: 'GRANT_PERSONAL_CREDITS',
        userId,
        creditAmount: grantCreditAmount,
        reason: reasonText,
      })
      if (res.ok) {
        showFeedbackToast(`Successfully granted ${grantCreditAmount} credits.`)
        setGrantCreditsOpen(false)
        setGrantCustomReason('')
        loadDetail(activeRevealToken)
      } else {
        showFeedbackToast(getErrorMessage(res, 'Failed to grant credits.'), true)
      }
    } catch (err: unknown) {
      showFeedbackToast(getErrorMessage(err, 'Failed to grant credits.'), true)
    } finally {
      setGrantCreditsBusy(false)
    }
  }

  // 2. Reset Gamification
  const handleConfirmResetGamification = async (reason: string) => {
    try {
      const res = await runOwnerAction({
        kind: 'RESET_GAMIFICATION_FOR_USER',
        userId,
        reason,
      })
      if (res.ok) {
        showFeedbackToast('Gamification zeroed and signup bonus flag reset.')
        setResetGamificationOpen(false)
        loadDetail(activeRevealToken)
      } else {
        showFeedbackToast(getErrorMessage(res, 'Action failed.'), true)
      }
    } catch (err: unknown) {
      showFeedbackToast(getErrorMessage(err, 'Action failed.'), true)
    }
  }

  // 3. Reset AI History
  const handleConfirmResetAiHistory = async (reason: string) => {
    try {
      const res = await runOwnerAction({
        kind: 'RESET_AI_HISTORY_FOR_USER',
        userId,
        reason,
      })
      if (res.ok) {
        showFeedbackToast('AI chat sessions, context cache, and proposals cleared.')
        setResetAiHistoryOpen(false)
        loadDetail(activeRevealToken)
      } else {
        showFeedbackToast(getErrorMessage(res, 'Action failed.'), true)
      }
    } catch (err: unknown) {
      showFeedbackToast(getErrorMessage(err, 'Action failed.'), true)
    }
  }

  // 4. Force Onboarding
  const handleConfirmForceOnboarding = async (reason: string) => {
    try {
      const res = await runOwnerAction({
        kind: 'FORCE_ONBOARDING_FOR_USER',
        userId,
        reason,
      })
      if (res.ok) {
        showFeedbackToast('Onboarding replay flag restored.')
        setForceOnboardingOpen(false)
        loadDetail(activeRevealToken)
      } else {
        showFeedbackToast(getErrorMessage(res, 'Action failed.'), true)
      }
    } catch (err: unknown) {
      showFeedbackToast(getErrorMessage(err, 'Action failed.'), true)
    }
  }

  // 5. Force Sign-Out Everywhere
  const handleConfirmSignOutEverywhere = async (reason: string) => {
    try {
      const res = await runOwnerAction({
        kind: 'FORCE_SIGN_OUT_EVERYWHERE',
        userId,
        reason,
      })
      if (res.ok) {
        showFeedbackToast('Refresh tokens revoked. All active sessions invalidated.')
        setSignOutEverywhereOpen(false)
        loadDetail(activeRevealToken)
      } else {
        showFeedbackToast(getErrorMessage(res, 'Action failed.'), true)
      }
    } catch (err: unknown) {
      showFeedbackToast(getErrorMessage(err, 'Action failed.'), true)
    }
  }

  // 6. Force Password Reset
  const handleConfirmPasswordReset = async (reason: string) => {
    try {
      const res = await runOwnerAction({
        kind: 'FORCE_PASSWORD_RESET',
        userId,
        reason,
      })
      if (res.ok) {
        showFeedbackToast(`Password reset link generated and sent to ${detail?.person.email}.`)
        setPasswordResetOpen(false)
        loadDetail(activeRevealToken)
      } else {
        showFeedbackToast(getErrorMessage(res, 'Action failed.'), true)
      }
    } catch (err: unknown) {
      showFeedbackToast(getErrorMessage(err, 'Action failed.'), true)
    }
  }

  // 7. Toggle Account Disabled
  const handleConfirmToggleDisabled = async (reason: string) => {
    const nextDisabled = !detail?.person.disabled
    try {
      const res = await runOwnerAction({
        kind: 'TOGGLE_ACCOUNT_DISABLED',
        userId,
        enabled: !nextDisabled,
        reason,
      })
      if (res.ok) {
        showFeedbackToast(nextDisabled ? 'Account disabled.' : 'Account enabled.')
        setToggleDisabledOpen(false)
        loadDetail(activeRevealToken)
      } else {
        showFeedbackToast(getErrorMessage(res, 'Action failed.'), true)
      }
    } catch (err: unknown) {
      showFeedbackToast(getErrorMessage(err, 'Action failed.'), true)
    }
  }

  // Columns for Workspaces (Tab 2)
  const workspaceColumns: ColumnDef<OwnerPersonWorkspaceItem>[] = useMemo(
    () => [
      {
        key: 'workspace',
        header: 'Workspace',
        render: (row) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontWeight: 600, color: 'var(--text-hi)' }}>{row.name}</span>
            <span style={{ fontSize: 11, color: 'var(--text-low)' }}>{row.id}</span>
          </div>
        ),
      },
      {
        key: 'role',
        header: 'Role',
        width: 120,
        render: (row) => (
          <span style={{ fontSize: 12, fontWeight: 500, color: row.role === 'Owner' ? 'var(--accent)' : 'var(--text-hi)' }}>
            {row.role}
          </span>
        ),
      },
      {
        key: 'plan',
        header: 'Plan',
        width: 110,
        render: (row) => (
          <span className={`ownerPeoplePlanPill ownerPeoplePlanPill--${row.plan.toLowerCase()}`}>
            {row.plan}
          </span>
        ),
      },
      {
        key: 'lastActive',
        header: 'Last Active',
        width: 130,
        render: (row) => (
          <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
            {row.lastActiveAt ? new Date(row.lastActiveAt).toLocaleDateString() : 'Never'}
          </span>
        ),
      },
      {
        key: 'alerts',
        header: 'Alerts',
        width: 120,
        render: (row) => (
          <span style={{ fontSize: 12, color: row.alerts !== '—' ? 'var(--warn)' : 'var(--text-low)' }}>
            {row.alerts}
          </span>
        ),
      },
    ],
    [],
  )

  // Columns for Tickets (Tab 4)
  const ticketColumns: ColumnDef<SupportTicket>[] = useMemo(
    () => [
      {
        key: 'ticket',
        header: 'Ticket',
        render: (row) => {
          const legacySubject = 'subject' in row && typeof row.subject === 'string' ? row.subject : null
          const displayTitle = row.title || legacySubject || `Ticket #${row.id.slice(0, 8)}`
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontWeight: 600, color: 'var(--text-hi)' }}>{displayTitle}</span>
              <span style={{ fontSize: 11, color: 'var(--text-low)' }}>#{row.id.slice(0, 8)}</span>
            </div>
          )
        },
      },
      {
        key: 'priority',
        header: 'Priority',
        width: 90,
        render: (row) => (
          <span className={`ownerTicketPriorityBadge ownerTicketPriorityBadge--${(row.priority || 'P2').toLowerCase()}`}>
            {row.priority || 'P2'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: 130,
        render: (row) => (
          <span style={{ fontSize: 12, textTransform: 'capitalize', color: 'var(--text-mid)' }}>
            {row.status || 'new'}
          </span>
        ),
      },
      {
        key: 'created',
        header: 'Created / SLA',
        width: 160,
        render: (row) => {
          const toDateObj = (val: unknown): Date | null => {
            if (!val) return null
            if (val instanceof Date) return val
            if (typeof val === 'object' && val !== null && 'toDate' in val && typeof (val as { toDate: unknown }).toDate === 'function') {
              return ((val as { toDate: () => Date }).toDate)()
            }
            if (typeof val === 'number') return new Date(val)
            if (typeof val === 'string') return new Date(val)
            return null
          }
          const createdDate = toDateObj(row.createdAt)
          const created = createdDate ? createdDate.toLocaleDateString() : '—'
          let deadlineText = ''
          if (createdDate && row.priority) {
            const nominal = calculateNominalDeadline(createdDate, row.priority as SupportTicketPriority)
            const msLeft = nominal - Date.now()
            if (msLeft < 0) {
              deadlineText = 'Breached'
            } else {
              const hoursLeft = Math.round(msLeft / (60 * 60 * 1000))
              deadlineText = hoursLeft > 24 ? `${Math.round(hoursLeft / 24)}d left` : `${hoursLeft}h left`
            }
          }
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--text-hi)' }}>{created}</span>
              {deadlineText && (
                <span style={{ fontSize: 11, color: deadlineText === 'Breached' ? 'var(--danger)' : 'var(--ok)' }}>
                  SLA: {deadlineText}
                </span>
              )}
            </div>
          )
        },
      },
    ],
    [],
  )

  // Columns for Activity (Tab 6)
  const activityColumns: ColumnDef<OwnerPersonDetailBundle['recentActions'][number]>[] = useMemo(
    () => [
      {
        key: 'action',
        header: 'Action',
        render: (row) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontWeight: 600, color: 'var(--text-hi)' }}>{row.kind}</span>
            <span style={{ fontSize: 11, color: 'var(--text-low)' }}>{row.id}</span>
          </div>
        ),
      },
      {
        key: 'actor',
        header: 'Actor',
        width: 140,
        render: (row) => (
          <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
            {row.actorRole || 'founder'}
          </span>
        ),
      },
      {
        key: 'reason',
        header: 'Reason',
        render: (row) => (
          <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
            {row.reason || 'No reason specified'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: 100,
        render: (row) => (
          <span style={{ fontSize: 12, color: row.status === 'SUCCESS' ? 'var(--ok)' : 'var(--danger)' }}>
            {row.status}
          </span>
        ),
      },
      {
        key: 'time',
        header: 'Time',
        width: 130,
        render: (row) => (
          <span style={{ fontSize: 12, color: 'var(--text-low)' }}>
            {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'}
          </span>
        ),
      },
    ],
    [],
  )

  // Columns for Console Access History (Tab 8)
  const accessHistoryColumns: ColumnDef<OwnerPersonBreakGlassGrantItem>[] = useMemo(
    () => [
      {
        key: 'reason',
        header: 'Reason',
        render: (row) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontWeight: 600, color: 'var(--text-hi)' }}>{row.reason}</span>
            <span style={{ fontSize: 11, color: 'var(--text-low)' }}>Grant #{row.grantId.slice(0, 8)}</span>
          </div>
        ),
      },
      {
        key: 'target',
        header: 'Target / Scope',
        width: 180,
        render: (row) => (
          <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
            {row.linkedId || 'user:content:read'}
          </span>
        ),
      },
      {
        key: 'duration',
        header: 'Duration',
        width: 100,
        render: (row) => (
          <span style={{ fontSize: 12, color: 'var(--text-hi)' }}>
            {row.durationSec}s
          </span>
        ),
      },
      {
        key: 'actor',
        header: 'Operator',
        width: 180,
        render: (row) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 12, color: 'var(--text-hi)' }}>{row.actorEmail}</span>
            <span style={{ fontSize: 11, color: 'var(--text-low)' }}>{row.actorRole}</span>
          </div>
        ),
      },
      {
        key: 'time',
        header: 'Timestamp',
        width: 130,
        render: (row) => (
          <span style={{ fontSize: 12, color: 'var(--text-low)' }}>
            {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'}
          </span>
        ),
      },
    ],
    [],
  )

  if (loading) {
    return (
      <div className="ownerPersonDetailContainer" data-testid="owner-person-detail-loading" style={{ padding: 32 }}>
        <p style={{ color: 'var(--text-mid)' }}>Loading person profile…</p>
      </div>
    )
  }

  if (error || !detail) {
    return (
      <div className="ownerPersonDetailContainer" data-testid="owner-person-detail-error" style={{ padding: 32 }}>
        <button type="button" className="ownerPersonActionBtn" onClick={handleBack} style={{ marginBottom: 16 }}>
          <ArrowBackRoundedIcon fontSize="small" /> Back to People
        </button>
        <p style={{ color: 'var(--danger)' }}>{error || 'User not found'}</p>
      </div>
    )
  }

  const { person, kpi, restrictions } = detail
  const workspaces = detail.workspaces || []
  const wallet = detail.wallet || {
    availableCredits: kpi?.creditsAvailable || 0,
    lifetimeSpentCredits: kpi?.creditsSpentLifetime || 0,
    lifetimeGrantedCredits: 0,
    lifetimePurchasedCredits: 0,
    lastGrantedAt: null,
  }
  const feedback = detail.feedback || []
  const breakglassGrants = detail.breakglassGrants || []
  const inTheirWords = detail.inTheirWords || { latestFeedbackHeadline: null, latestTicketSubject: null }
  const recentActions = detail.recentActions || []
  const initial = (person.email?.[0] || person.displayName?.[0] || 'U').toUpperCase()

  const joinedFormatted = person.createdAt
    ? new Date(person.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'Unknown'

  const lastActiveFormatted = person.lastActiveAt
    ? new Date(person.lastActiveAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'Never'

  const hasActiveRestrictions = Boolean(
    restrictions &&
      (restrictions.blockNewTickets ||
        restrictions.blockReopens ||
        restrictions.slowMode ||
        (Array.isArray(restrictions.autoTag) && restrictions.autoTag.length > 0) ||
        restrictions.skipAutoP0 ||
        restrictions.broadcastOptOut),
  )

  const ownedWorkspaces = workspaces.filter((w) => w.role === 'Owner')

  return (
    <div className="ownerPersonDetailContainer" data-testid="owner-person-detail">
      {/* Toast Messages */}
      {actionSuccessMsg && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: 'var(--ok)', color: '#fff', padding: '10px 16px', borderRadius: 6, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.4)', fontSize: 13, fontWeight: 500 }}>
          {actionSuccessMsg}
        </div>
      )}
      {actionErrorMsg && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: 'var(--danger)', color: '#fff', padding: '10px 16px', borderRadius: 6, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.4)', fontSize: 13, fontWeight: 500 }}>
          {actionErrorMsg}
        </div>
      )}

      {/* 1. Header */}
      <header className="ownerPersonDetailHeader">
        <div className="ownerPersonDetailHeaderTop">
          <div className="ownerPersonDetailProfile">
            <button
              type="button"
              className="ownerPersonActionBtn"
              onClick={handleBack}
              title="Back to people list"
              style={{ padding: '6px 8px' }}
            >
              <ArrowBackRoundedIcon fontSize="small" />
            </button>
            <div className="ownerPersonDetailAvatar">{initial}</div>
            <div className="ownerPersonDetailMetaGroup">
              <div className="ownerPersonDetailEmailRow">
                <span className="ownerPersonDetailEmail">{person.email}</span>
                <span className={`ownerPeoplePlanPill ownerPeoplePlanPill--${person.plan.toLowerCase()}`}>
                  {person.plan}
                </span>
                {person.disabled && (
                  <span className="ownerPeopleRestrictedPill" style={{ background: 'rgba(226, 107, 107, 0.2)', color: 'var(--danger)' }}>
                    Disabled
                  </span>
                )}
                {kpi.openTickets > 0 && (
                  <span className="ownerPeopleTicketPill">
                    {kpi.openTickets} open {kpi.openTickets === 1 ? 'ticket' : 'tickets'}
                  </span>
                )}
                {hasActiveRestrictions && (
                  <span className="ownerPeopleRestrictedPill">Restricted</span>
                )}
              </div>
              <div className="ownerPersonDetailSubline">
                <span>{person.displayName || 'No display name'}</span>
                <span>·</span>
                <span>
                  UID: <code className="ownerPersonUidTruncated">{userId.slice(0, 8)}…</code>
                  <button
                    type="button"
                    className="ownerPersonCopyBtn"
                    onClick={handleCopyUid}
                    title="Copy full UID"
                  >
                    {copiedUid ? <CheckRoundedIcon style={{ fontSize: 13, color: 'var(--ok)' }} /> : <ContentCopyRoundedIcon style={{ fontSize: 13 }} />}
                  </button>
                </span>
                <span>·</span>
                <span>Joined {joinedFormatted}</span>
                <span>·</span>
                <span>Last active {lastActiveFormatted}</span>
              </div>
            </div>
          </div>

          <div className="ownerPersonDetailActionsBar">
            <button
              type="button"
              className="ownerPersonActionBtn ownerPersonActionBtn--primary"
              onClick={() => navigateOwner({ view: 'inbox', query: { q: person.email } })}
            >
              <ReplyRoundedIcon fontSize="small" /> Reply to open ticket
            </button>
            <button
              type="button"
              className="ownerPersonActionBtn"
              onClick={() => showFeedbackToast('Direct broadcast compose arrives in Phase 4.')}
            >
              <EmailRoundedIcon fontSize="small" /> Send message
            </button>
            <button
              type="button"
              className="ownerPersonActionBtn"
              onClick={() => setGrantCreditsOpen(true)}
            >
              <TollRoundedIcon fontSize="small" /> Grant credits
            </button>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="ownerPersonActionBtn"
                style={{ padding: '6px 8px' }}
                title="More actions"
                onClick={() => setOpenMoreMenu(!openMoreMenu)}
              >
                <MoreHorizRoundedIcon fontSize="small" />
              </button>
              {openMoreMenu && (
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: '100%',
                    background: 'var(--surface-2, #1a2029)',
                    border: '1px solid var(--divider, #232b36)',
                    borderRadius: 6,
                    padding: 4,
                    zIndex: 100,
                    minWidth: 190,
                    boxShadow: '0 6px 16px rgba(0,0,0,0.6)',
                  }}
                  onClick={() => setOpenMoreMenu(false)}
                >
                  <button
                    type="button"
                    style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--text-hi)', padding: '7px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 6 }}
                    onClick={() => setForceOnboardingOpen(true)}
                  >
                    <RestartAltRoundedIcon fontSize="small" /> Force Onboarding
                  </button>
                  <button
                    type="button"
                    style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--text-hi)', padding: '7px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 6 }}
                    onClick={() => setResetGamificationOpen(true)}
                  >
                    <SportsEsportsRoundedIcon fontSize="small" /> Reset Gamification
                  </button>
                  <button
                    type="button"
                    style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--text-hi)', padding: '7px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 6 }}
                    onClick={() => setResetAiHistoryOpen(true)}
                  >
                    <SmartToyRoundedIcon fontSize="small" /> Reset AI History
                  </button>
                  <div style={{ height: 1, background: 'var(--divider)', margin: '4px 0' }} />
                  <button
                    type="button"
                    style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', color: person.disabled ? 'var(--ok)' : 'var(--danger)', padding: '7px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 6 }}
                    onClick={() => setToggleDisabledOpen(true)}
                  >
                    <BlockRoundedIcon fontSize="small" /> {person.disabled ? 'Enable Account' : 'Disable Account'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* 2. Tabs Bar */}
      <nav className="ownerPersonDetailTabs" aria-label="Person detail tabs">
        {ALL_TABS.map((tab) => {
          const isActive = activeTab === tab.id
          return (
            <button
              key={tab.id}
              type="button"
              className={`ownerPersonTab ${isActive ? 'ownerPersonTab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          )
        })}
      </nav>

      {/* 3. Tab Body */}
      <main className="ownerPersonDetailBody">
        {/* Tab 1: Overview */}
        {activeTab === 'overview' && (
          <div className="ownerOverviewGrid" data-testid="owner-person-overview">
            {/* KPI Grid */}
            <section className="ownerKpiGrid" aria-label="User key performance indicators">
              <div className="ownerKpiCard">
                <span className="ownerKpiLabel">Workspaces Owned</span>
                <span className="ownerKpiValue">{kpi.workspacesOwned}</span>
                <span className="ownerKpiSub">Owner role</span>
              </div>
              <div className="ownerKpiCard">
                <span className="ownerKpiLabel">Member Of</span>
                <span className="ownerKpiValue">{kpi.workspacesMemberOf}</span>
                <span className="ownerKpiSub">Workspaces joined</span>
              </div>
              <div className="ownerKpiCard">
                <span className="ownerKpiLabel">Credits Available</span>
                <span className="ownerKpiValue">{kpi.creditsAvailable.toLocaleString()} Cr</span>
                <span className="ownerKpiSub">Personal wallet</span>
              </div>
              <div className="ownerKpiCard">
                <span className="ownerKpiLabel">Credits Spent Lifetime</span>
                <span className="ownerKpiValue">{kpi.creditsSpentLifetime.toLocaleString()} Cr</span>
                <span className="ownerKpiSub">Historical spend</span>
              </div>
              <div className="ownerKpiCard">
                <span className="ownerKpiLabel">Open Tickets</span>
                <span className="ownerKpiValue">{kpi.openTickets}</span>
                <span className="ownerKpiSub">Awaiting resolution</span>
              </div>
            </section>

            {/* In Their Words Panel */}
            <section className="ownerSectionCard" aria-label="In their words">
              <h2 className="ownerSectionTitle">In Their Words</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="ownerInTheirWordsRow">
                  <span className="ownerInTheirWordsLabel">Latest Feedback</span>
                  <span className="ownerInTheirWordsHeadline">
                    {inTheirWords.latestFeedbackHeadline || 'No feedback responses submitted yet.'}
                  </span>
                </div>
                <div className="ownerInTheirWordsRow">
                  <span className="ownerInTheirWordsLabel">Latest Ticket Subject</span>
                  <span className="ownerInTheirWordsHeadline">
                    {inTheirWords.latestTicketSubject || 'No tickets filed yet.'}
                  </span>
                </div>
              </div>
            </section>

            {/* User Controls Action Triggers (Migrated from Controls catalog) */}
            <section className="ownerSectionCard" aria-label="Account management actions">
              <h2 className="ownerSectionTitle">Account Operations</h2>
              <p style={{ color: 'var(--text-mid)', fontSize: 13, margin: 0 }}>
                Targeted actions bound directly to this account context.
              </p>
              <div className="ownerDetailActionRow">
                <button
                  type="button"
                  className="ownerPersonActionBtn"
                  onClick={() => setGrantCreditsOpen(true)}
                >
                  <TollRoundedIcon fontSize="small" /> Grant Personal Credits
                </button>
                <button
                  type="button"
                  className="ownerPersonActionBtn"
                  onClick={() => setForceOnboardingOpen(true)}
                >
                  <RestartAltRoundedIcon fontSize="small" /> Force Onboarding Tour
                </button>
                <button
                  type="button"
                  className="ownerPersonActionBtn"
                  style={{ color: 'var(--danger)', borderColor: 'rgba(226, 107, 107, 0.4)' }}
                  onClick={() => setResetGamificationOpen(true)}
                >
                  <SportsEsportsRoundedIcon fontSize="small" /> Reset Gamification
                </button>
                <button
                  type="button"
                  className="ownerPersonActionBtn"
                  style={{ color: 'var(--danger)', borderColor: 'rgba(226, 107, 107, 0.4)' }}
                  onClick={() => setResetAiHistoryOpen(true)}
                >
                  <SmartToyRoundedIcon fontSize="small" /> Reset AI History
                </button>
              </div>
            </section>

            {/* Recent Actions On This User */}
            <section className="ownerSectionCard" aria-label="Recent actions on this user">
              <h2 className="ownerSectionTitle">Recent Actions on this User</h2>
              {recentActions.length === 0 ? (
                <p style={{ color: 'var(--text-mid)', fontSize: 13, margin: 0 }}>
                  No operator actions recorded for this user yet.
                </p>
              ) : (
                <div className="ownerActionsList">
                  {recentActions.map((act) => (
                    <div key={act.id} className="ownerActionItem">
                      <div>
                        <span className="ownerActionKind">{act.kind}</span>
                        {act.reason && <span style={{ color: 'var(--text-mid)', marginLeft: 8 }}>({act.reason})</span>}
                      </div>
                      <span className="ownerActionMeta">
                        by {act.actorRole} · {new Date(act.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Restrictions Section */}
            <section className="ownerSectionCard" aria-label="User restrictions">
              <h2 className="ownerSectionTitle">Restrictions</h2>
              {hasActiveRestrictions ? (
                <div className="ownerRestrictionActiveCard">
                  <strong style={{ color: 'var(--warn)', fontSize: 13 }}>Active Restrictions Applied</strong>
                  <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 12, color: 'var(--text-hi)' }}>
                    {restrictions?.blockNewTickets && <li>Blocked from opening new tickets</li>}
                    {restrictions?.blockReopens && <li>Blocked from reopening resolved tickets</li>}
                    {restrictions?.slowMode && <li>Slow mode active ({restrictions.slowMode} max per day)</li>}
                    {restrictions?.skipAutoP0 && <li>Auto-P0 promotion bypassed</li>}
                    {restrictions?.broadcastOptOut && <li>Opted out of marketing broadcasts</li>}
                  </ul>
                  {restrictions?.reason && (
                    <span style={{ fontSize: 11, color: 'var(--text-mid)' }}>Reason: {restrictions.reason}</span>
                  )}
                </div>
              ) : (
                <p style={{ color: 'var(--text-mid)', fontSize: 13, margin: 0 }}>
                  No restrictions active. Account is in good standing.
                </p>
              )}
            </section>

            {/* Seam for Cohort Membership Chips (Phase 3) */}
            <div className="ownerSeamCard" data-testid="seam-cohorts">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <InfoOutlinedIcon style={{ fontSize: 16, color: 'var(--text-mid)' }} />
                <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
                  Cohort membership segmentation arrives with <code>owner_cohorts</code>.
                </span>
              </div>
              <span className="ownerSeamBadge">Phase 3 Seam</span>
            </div>
          </div>
        )}

        {/* Tab 2: Workspaces */}
        {activeTab === 'workspaces' && (
          <div className="ownerDetailTabPane" data-testid="owner-person-workspaces">
            <section className="ownerSectionCard">
              <h2 className="ownerSectionTitle">
                Workspaces ({workspaces.length})
              </h2>
              {workspaces.length === 0 ? (
                <p style={{ color: 'var(--text-mid)', fontSize: 13, margin: 0 }}>
                  No workspaces owned or joined by this user yet.
                </p>
              ) : (
                <OwnerTable
                  data={workspaces}
                  columns={workspaceColumns}
                  viewKey="owner-person-workspaces"
                  getRowId={(r) => r.id}
                  onRowClick={(row) => navigateOwner({ view: 'workspace-detail', workspaceId: row.id, query: { tab: 'overview' } })}
                  pageSize={25}
                />
              )}
            </section>
          </div>
        )}

        {/* Tab 3: Subscriptions & credits */}
        {activeTab === 'subscriptions' && (
          <div className="ownerDetailTabPane" data-testid="owner-person-subscriptions">
            <div className="ownerDetailCardGrid">
              {/* Personal Wallet */}
              <section className="ownerSectionCard">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2 className="ownerSectionTitle">Personal Wallet</h2>
                  <button
                    type="button"
                    className="ownerPersonActionBtn ownerPersonActionBtn--primary"
                    onClick={() => setGrantCreditsOpen(true)}
                  >
                    <TollRoundedIcon fontSize="small" /> Grant credits
                  </button>
                </div>
                <div className="ownerDetailSubSection">
                  <div className="ownerDetailFieldRow">
                    <span className="ownerDetailFieldLabel">Available balance</span>
                    <span className="ownerDetailFieldValue" style={{ color: 'var(--ok)', fontSize: 15, fontWeight: 600 }}>
                      {wallet.availableCredits.toLocaleString()} Cr
                    </span>
                  </div>
                  <div className="ownerDetailFieldRow">
                    <span className="ownerDetailFieldLabel">Lifetime spend</span>
                    <span className="ownerDetailFieldValue">
                      {wallet.lifetimeSpentCredits.toLocaleString()} Cr
                    </span>
                  </div>
                  <div className="ownerDetailFieldRow">
                    <span className="ownerDetailFieldLabel">Lifetime granted</span>
                    <span className="ownerDetailFieldValue">
                      {wallet.lifetimeGrantedCredits.toLocaleString()} Cr
                    </span>
                  </div>
                  <div className="ownerDetailFieldRow">
                    <span className="ownerDetailFieldLabel">Last top-up</span>
                    <span className="ownerDetailFieldValue">
                      {wallet.lastGrantedAt ? new Date(wallet.lastGrantedAt).toLocaleDateString() : 'Never'}
                    </span>
                  </div>
                </div>
              </section>

              {/* Team Subscriptions */}
              <section className="ownerSectionCard">
                <h2 className="ownerSectionTitle">Owned Team Subscriptions</h2>
                {ownedWorkspaces.length === 0 ? (
                  <p style={{ color: 'var(--text-mid)', fontSize: 13, margin: 0 }}>
                    This user does not own any workspaces with active team subscriptions.
                  </p>
                ) : (
                  <div className="ownerDetailSubSection">
                    {ownedWorkspaces.map((ws) => (
                      <div key={ws.id} className="ownerDetailFieldRow">
                        <div>
                          <span style={{ fontWeight: 600, color: 'var(--text-hi)' }}>{ws.name}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-low)', display: 'block' }}>{ws.seatCount} seats</span>
                        </div>
                        <span className={`ownerPeoplePlanPill ownerPeoplePlanPill--${ws.plan.toLowerCase()}`}>
                          {ws.plan}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>

            {/* Refund Purchase Seam */}
            <div className="ownerSeamCard" data-testid="seam-refund-purchase">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <InfoOutlinedIcon style={{ fontSize: 16, color: 'var(--text-mid)' }} />
                <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
                  Refund purchase ledger adjustments require Finance role and arrive in Phase 2.
                </span>
              </div>
              <span className="ownerSeamBadge">Phase 2 Seam</span>
            </div>
          </div>
        )}

        {/* Tab 4: Tickets */}
        {activeTab === 'tickets' && (
          <div className="ownerDetailTabPane" data-testid="owner-person-tickets">
            <section className="ownerSectionCard">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 className="ownerSectionTitle">
                  Tickets Filed by this User ({tickets.length})
                </h2>
                <button
                  type="button"
                  className="ownerPersonActionBtn ownerPersonActionBtn--primary"
                  onClick={() => navigateOwner({ view: 'inbox', query: { q: person.email } })}
                >
                  <ReplyRoundedIcon fontSize="small" /> Open in Support Inbox
                </button>
              </div>

              {ticketsLoading ? (
                <div style={{ padding: 24, color: 'var(--text-mid)' }}>Loading tickets…</div>
              ) : tickets.length === 0 ? (
                <p style={{ color: 'var(--text-mid)', fontSize: 13, margin: '12px 0 0 0' }}>
                  No support tickets reported by this user yet.
                </p>
              ) : (
                <OwnerTable
                  data={tickets}
                  columns={ticketColumns}
                  viewKey="owner-person-tickets"
                  getRowId={(r) => r.id}
                  onRowClick={(r) => navigateOwner({ view: 'ticket-detail', ticketId: r.id })}
                  pageSize={25}
                />
              )}
            </section>
          </div>
        )}

        {/* Tab 5: Feedback */}
        {activeTab === 'feedback' && (
          <div className="ownerDetailTabPane" data-testid="owner-person-feedback">
            <section className="ownerSectionCard">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 className="ownerSectionTitle">
                  Feedback Responses ({feedback.length})
                </h2>
                {feedback.some((f) => f.remarkRedacted) && (
                  <button
                    type="button"
                    className="ownerPersonActionBtn"
                    style={{ borderColor: 'var(--warn)', color: 'var(--warn)' }}
                    onClick={() => setBreakGlassOpen(true)}
                  >
                    <SecurityRoundedIcon fontSize="small" /> Break-Glass to Reveal Remarks
                  </button>
                )}
              </div>

              {feedback.length === 0 ? (
                <p style={{ color: 'var(--text-mid)', fontSize: 13, margin: '12px 0 0 0' }}>
                  No feedback submissions on record for this user.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
                  {feedback.map((fb) => (
                    <div key={fb.id} className="ownerDetailFieldRow" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8, padding: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <div>
                          <strong style={{ fontSize: 13, color: 'var(--text-hi)' }}>{fb.formId} Feedback</strong>
                          {fb.rating && (
                            <span style={{ marginLeft: 8, color: 'var(--warn)', fontWeight: 600 }}>{fb.rating}★</span>
                          )}
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--text-low)' }}>
                          {fb.submittedAt ? new Date(fb.submittedAt).toLocaleDateString() : '—'}
                        </span>
                      </div>

                      {/* Remark body display */}
                      {fb.remarks ? (
                        <div className="ownerFeedbackRemarkBox">
                          "{fb.remarks}"
                        </div>
                      ) : fb.remarkRedacted ? (
                        <div className="ownerFeedbackGatedBox" style={{ width: '100%' }}>
                          <span style={{ fontSize: 12, color: 'var(--warn)' }}>
                            Free-text remark body is redacted behind content privacy rules.
                          </span>
                          <button
                            type="button"
                            className="ownerPersonActionBtn"
                            style={{ fontSize: 11, padding: '4px 8px' }}
                            onClick={() => setBreakGlassOpen(true)}
                          >
                            Reveal Body
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--text-low)', fontStyle: 'italic' }}>
                          No text remark provided with rating.
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Authored Survey Campaigns Seam */}
            <div className="ownerSeamCard" data-testid="seam-authored-surveys">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <InfoOutlinedIcon style={{ fontSize: 16, color: 'var(--text-mid)' }} />
                <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
                  Authoring and scheduled multi-question survey responses arrive in Phase 4.
                </span>
              </div>
              <span className="ownerSeamBadge">Phase 4 Seam</span>
            </div>
          </div>
        )}

        {/* Tab 6: Activity */}
        {activeTab === 'activity' && (
          <div className="ownerDetailTabPane" data-testid="owner-person-activity">
            <section className="ownerSectionCard">
              <h2 className="ownerSectionTitle">
                Operator Audit Trail ({recentActions.length})
              </h2>
              {recentActions.length === 0 ? (
                <p style={{ color: 'var(--text-mid)', fontSize: 13, margin: '12px 0 0 0' }}>
                  No operator actions targeting this user recorded in <code>owner_action_log</code>.
                </p>
              ) : (
                <OwnerTable
                  data={recentActions}
                  columns={activityColumns}
                  viewKey="owner-person-activity"
                  getRowId={(r) => r.id}
                  pageSize={25}
                />
              )}
            </section>
          </div>
        )}

        {/* Tab 7: Sessions & devices */}
        {activeTab === 'sessions' && (
          <div className="ownerDetailTabPane" data-testid="owner-person-sessions">
            <div className="ownerDetailCardGrid">
              {/* Auth Metadata */}
              <section className="ownerSectionCard">
                <h2 className="ownerSectionTitle">Authentication State</h2>
                <div className="ownerDetailSubSection">
                  <div className="ownerDetailFieldRow">
                    <span className="ownerDetailFieldLabel">Last sign-in time</span>
                    <span className="ownerDetailFieldValue">
                      {person.lastSignInTime ? new Date(person.lastSignInTime).toLocaleString() : 'No sign-in on record'}
                    </span>
                  </div>
                  <div className="ownerDetailFieldRow">
                    <span className="ownerDetailFieldLabel">Last token refresh</span>
                    <span className="ownerDetailFieldValue">
                      {person.lastRefreshTime ? new Date(person.lastRefreshTime).toLocaleString() : 'Never refreshed'}
                    </span>
                  </div>
                  <div className="ownerDetailFieldRow">
                    <span className="ownerDetailFieldLabel">Account status</span>
                    <span className="ownerDetailFieldValue" style={{ color: person.disabled ? 'var(--danger)' : 'var(--ok)' }}>
                      {person.disabled ? 'Disabled' : 'Active'}
                    </span>
                  </div>
                </div>
              </section>

              {/* Security Levers */}
              <section className="ownerSectionCard">
                <h2 className="ownerSectionTitle">Security Levers</h2>
                <p style={{ color: 'var(--text-mid)', fontSize: 13, margin: 0 }}>
                  Immediate account security interventions audited to <code>owner_action_log</code>.
                </p>
                <div className="ownerDetailActionRow" style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    className="ownerPersonActionBtn"
                    onClick={() => setSignOutEverywhereOpen(true)}
                  >
                    <ExitToAppRoundedIcon fontSize="small" /> Force Sign-Out Everywhere
                  </button>
                  <button
                    type="button"
                    className="ownerPersonActionBtn"
                    onClick={() => setPasswordResetOpen(true)}
                  >
                    <LockResetRoundedIcon fontSize="small" /> Force Password Reset
                  </button>
                  <button
                    type="button"
                    className="ownerPersonActionBtn"
                    style={{ color: person.disabled ? 'var(--ok)' : 'var(--danger)', borderColor: person.disabled ? 'rgba(63, 178, 127, 0.4)' : 'rgba(226, 107, 107, 0.4)' }}
                    onClick={() => setToggleDisabledOpen(true)}
                  >
                    {person.disabled ? <LockOpenRoundedIcon fontSize="small" /> : <BlockRoundedIcon fontSize="small" />}
                    {person.disabled ? 'Enable Account' : 'Disable Account'}
                  </button>
                </div>
              </section>
            </div>

            {/* Auth Logging Seam */}
            <div className="ownerSeamCard" data-testid="seam-auth-events">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <InfoOutlinedIcon style={{ fontSize: 16, color: 'var(--text-mid)' }} />
                <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
                  Sign-in event history logs (IP hash & user agent telemetry) arrive with the Cloud Logging export sink.
                </span>
              </div>
              <span className="ownerSeamBadge">Phase 1b Seam</span>
            </div>
          </div>
        )}

        {/* Tab 8: Data & privacy */}
        {activeTab === 'privacy' && (
          <div className="ownerDetailTabPane" data-testid="owner-person-privacy">
            {/* Console Access History */}
            <section className="ownerSectionCard">
              <h2 className="ownerSectionTitle">
                Console Access History ({breakglassGrants.length})
              </h2>
              <p style={{ color: 'var(--text-mid)', fontSize: 13, margin: 0 }}>
                Break-glass grants and operator content access targeting this user account.
              </p>
              {breakglassGrants.length === 0 ? (
                <p style={{ color: 'var(--text-mid)', fontSize: 13, margin: '12px 0 0 0' }}>
                  No console break-glass grants recorded for this user.
                </p>
              ) : (
                <div style={{ marginTop: 12 }}>
                  <OwnerTable
                    data={breakglassGrants}
                    columns={accessHistoryColumns}
                    viewKey="owner-person-access-history"
                    getRowId={(r) => r.grantId}
                    pageSize={20}
                  />
                </div>
              )}
            </section>

            {/* Owner Data Export Card */}
            <div className="ownerSeamCard" data-testid="seam-data-export">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <InfoOutlinedIcon style={{ fontSize: 16, color: 'var(--text-mid)' }} />
                    <strong style={{ fontSize: 13 }}>{t('NLSOwnerConsoleExportTitle')}</strong>
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
                    {t('NLSOwnerConsoleExportDesc')}
                  </span>
                  {exportJobs[0] && (
                    <span style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 2 }}>
                      {t('NLSOwnerConsoleExportStatus', { status: exportJobs[0].status })}
                    </span>
                  )}
                  {exportNotice && (
                    <span style={{ fontSize: 12, color: 'var(--danger-text, #ef4444)' }}>
                      {exportNotice}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="ownerPersonActionBtn"
                  disabled={exporting || exportJobs[0]?.status === 'QUEUED' || exportJobs[0]?.status === 'RUNNING'}
                  onClick={() => void handleOwnerExport()}
                >
                  {t('NLSOwnerConsoleExportButton')}
                </button>
              </div>
            </div>

            <div className="ownerSeamCard" data-testid="seam-user-redaction">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <InfoOutlinedIcon style={{ fontSize: 16, color: 'var(--text-mid)' }} />
                <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
                  Account redaction request and <code>REDACT_USER</code> runbook execution arrive in Phase 9.
                </span>
              </div>
              <span className="ownerSeamBadge">Phase 9 Seam</span>
            </div>
          </div>
        )}
      </main>

      {/* Grant Personal Credits Modal */}
      {grantCreditsOpen && (
        <div className="ownerModalOverlay" role="dialog" aria-modal="true" aria-labelledby="grant-credits-title">
          <div className="ownerModalDialog">
            <div className="ownerModalHeader">
              <h3 id="grant-credits-title">Grant Personal Credits</h3>
            </div>
            <div className="ownerModalBody">
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: 'var(--text-mid)' }}>
                Credit Amount
                <input
                  type="number"
                  className="ownerModalInput"
                  value={grantCreditAmount}
                  min={1}
                  max={role === 'support' ? 500 : 100000}
                  step={25}
                  onChange={(e) => setGrantCreditAmount(Math.max(1, Number(e.target.value || 1)))}
                  disabled={grantCreditsBusy}
                />
                {role === 'support' && (
                  <span style={{ fontSize: 11, color: 'var(--warn)' }}>Support role cap: 500 Cr max per grant</span>
                )}
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: 'var(--text-mid)', marginTop: 12 }}>
                Reason ENUM
                <select
                  className="ownerPeopleSortSelect"
                  style={{ width: '100%' }}
                  value={grantReasonEnum}
                  onChange={(e) => setGrantReasonEnum(e.target.value)}
                  disabled={grantCreditsBusy}
                >
                  {CREDIT_REASON_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: 'var(--text-mid)', marginTop: 12 }}>
                Reason Details (min 6 characters)
                <input
                  type="text"
                  className="ownerModalInput"
                  placeholder="e.g. Compensation for timeout during workflow"
                  value={grantCustomReason}
                  onChange={(e) => setGrantCustomReason(e.target.value)}
                  disabled={grantCreditsBusy}
                />
              </label>

              {/* Balance preview */}
              <div className="ownerCreditPreviewBox">
                <div className="ownerCreditPreviewLine">
                  <span>Current balance:</span>
                  <span>{wallet.availableCredits.toLocaleString()} Cr</span>
                </div>
                <div className="ownerCreditPreviewLine">
                  <span>Grant amount:</span>
                  <span>+{grantCreditAmount.toLocaleString()} Cr</span>
                </div>
                <div className="ownerCreditPreviewLine" style={{ borderTop: '1px solid var(--divider)', paddingTop: 6 }}>
                  <span style={{ fontWeight: 600, color: 'var(--text-hi)' }}>Resulting balance:</span>
                  <span className="ownerCreditPreviewHighlight">
                    {(wallet.availableCredits + grantCreditAmount).toLocaleString()} Cr
                  </span>
                </div>
              </div>
            </div>
            <div className="ownerModalActions">
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--default"
                onClick={() => setGrantCreditsOpen(false)}
                disabled={grantCreditsBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--primary"
                onClick={handleConfirmGrantCredits}
                disabled={grantCreditsBusy || (grantCustomReason.trim().length > 0 && grantCustomReason.trim().length < 6)}
              >
                {grantCreditsBusy ? 'Granting…' : 'Confirm Grant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Break-Glass Modal for Feedback Remarks */}
      <BreakGlassModal
        open={breakGlassOpen}
        subject={`users/${userId}/feedback`}
        onClose={() => setBreakGlassOpen(false)}
        onSuccess={(grant: BreakGlassGrantResult) => {
          setActiveRevealToken(grant.revealToken)
          setBreakGlassOpen(false)
          showFeedbackToast('Break-glass access granted. Remarks revealed.')
        }}
      />

      {/* Force Sign-Out Everywhere Modal (Firm Confirm) */}
      <OwnerFirmConfirm
        isOpen={signOutEverywhereOpen}
        title="Force sign-out everywhere"
        description="Revokes refresh tokens across all active sessions. The user will be required to sign in again immediately upon next navigation."
        onConfirm={handleConfirmSignOutEverywhere}
        onClose={() => setSignOutEverywhereOpen(false)}
        confirmLabel="Revoke Sessions"
      />

      {/* Force Password Reset Modal (Firm Confirm) */}
      <OwnerFirmConfirm
        isOpen={passwordResetOpen}
        title="Force password reset"
        description={`Triggers a Firebase Auth password reset email directly to ${person.email}. This sends an automated email to a real person.`}
        onConfirm={handleConfirmPasswordReset}
        onClose={() => setPasswordResetOpen(false)}
        confirmLabel="Send Reset Email"
      />

      {/* Force Onboarding Tour Modal (Firm Confirm) */}
      <OwnerFirmConfirm
        isOpen={forceOnboardingOpen}
        title="Force onboarding tour replay"
        description="Flips user onboarding completion flags back to false so the introduction and setup tour will replay when the user next visits."
        onConfirm={handleConfirmForceOnboarding}
        onClose={() => setForceOnboardingOpen(false)}
        confirmLabel="Reset Onboarding Flags"
      />

      {/* Reset Gamification Modal (Hard Confirm) */}
      <OwnerHardConfirm
        isOpen={resetGamificationOpen}
        title="Reset user gamification"
        targetHumanName={person.email}
        description="DESTRUCTIVE: Zeroes user XP, streak data, clears event dedupe keys, and resets signup bonus state. Type the user's email below to confirm."
        onConfirm={handleConfirmResetGamification}
        onClose={() => setResetGamificationOpen(false)}
      />

      {/* Reset AI History Modal (Hard Confirm) */}
      <OwnerHardConfirm
        isOpen={resetAiHistoryOpen}
        title="Reset AI session history"
        targetHumanName={person.email}
        description="DESTRUCTIVE: Clears all user AI chat sessions, proposals, context cache embeddings, and feedback drafts. Type the user's email below to confirm."
        onConfirm={handleConfirmResetAiHistory}
        onClose={() => setResetAiHistoryOpen(false)}
      />

      {/* Toggle Account Disabled Modal (Hard Confirm with Dry-Run Diff) */}
      <OwnerHardConfirm
        isOpen={toggleDisabledOpen}
        title={person.disabled ? 'Enable user account' : 'Disable user account'}
        targetHumanName={person.email}
        description={
          person.disabled
            ? 'Re-enables this user account. The user will be able to sign in and resume workspace collaboration.'
            : 'DESTRUCTIVE: Disabling this user account immediately revokes login access and affects all workspaces this user owns or belongs to.'
        }
        dryRunDiff={
          <div>
            {toggleDisabledOpen && !accountDisabled && lastOwnerWorkspaces.length > 0 && (
              <div role="alert" data-testid="owner-last-active-owner-warning" style={{ marginBottom: 8, color: 'var(--danger)', fontSize: 12 }}>
                {t('NLSOwnerConsolePeopleLastOwnerWarning', {
                  count: lastOwnerWorkspaces.length,
                  names: lastOwnerWorkspaces.map((w) => w.name).join(', '),
                })}
              </div>
            )}
            <span style={{ fontWeight: 600, color: 'var(--warn)', display: 'block', marginBottom: 4 }}>
              Impact on Associated Workspaces ({workspaces.length}):
            </span>
            {workspaces.length === 0 ? (
              <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>No active workspace memberships affected.</span>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--text-hi)' }}>
                {workspaces.map((w) => (
                  <li key={w.id}>
                    <strong>{w.name}</strong> ({w.role} · {w.seatCount} seats)
                  </li>
                ))}
              </ul>
            )}
          </div>
        }
        onConfirm={handleConfirmToggleDisabled}
        onClose={() => setToggleDisabledOpen(false)}
      />
    </div>
  )
}
